/**
 * Cálculo de ISR sobre asimilados a salarios (Art. 96 LISR).
 *
 * La tarifa es la mensual publicada por SAT, vigente 2024-2025 (la 2025
 * sale por anexo 8 de la RMF pero no cambió respecto a 2024). Para 2026
 * el SAT actualiza por inflación — si cuando lo veas el cálculo se siente
 * desfasado, revisa el Anexo 8 de la Resolución Miscelánea Fiscal vigente
 * y ajusta `ART96_BRACKETS`.
 *
 * Asimilados NO reciben subsidio para el empleo (eso es Capítulo I /
 * sueldos formales). La retención = tarifa directa sobre el bruto mensual.
 */

interface Bracket {
  /** Límite inferior en MXN (no centavos). */
  lower: number;
  /** Límite superior en MXN. Infinity en el último bracket. */
  upper: number;
  /** Cuota fija sobre el límite inferior, en MXN. */
  fixedQuota: number;
  /** Porcentaje sobre el excedente del límite inferior (0-1). */
  rate: number;
}

const ART96_BRACKETS: readonly Bracket[] = [
  { lower: 0.01, upper: 746.04, fixedQuota: 0, rate: 0.0192 },
  { lower: 746.05, upper: 6332.05, fixedQuota: 14.32, rate: 0.064 },
  { lower: 6332.06, upper: 11128.01, fixedQuota: 371.83, rate: 0.1088 },
  { lower: 11128.02, upper: 12935.82, fixedQuota: 893.63, rate: 0.16 },
  { lower: 12935.83, upper: 15487.71, fixedQuota: 1182.88, rate: 0.1792 },
  { lower: 15487.72, upper: 31236.49, fixedQuota: 1640.18, rate: 0.2136 },
  { lower: 31236.5, upper: 49233.0, fixedQuota: 5004.12, rate: 0.2352 },
  { lower: 49233.01, upper: 93993.9, fixedQuota: 9236.89, rate: 0.3 },
  { lower: 93993.91, upper: 125325.2, fixedQuota: 22665.17, rate: 0.32 },
  { lower: 125325.21, upper: 375975.61, fixedQuota: 32691.18, rate: 0.34 },
  { lower: 375975.62, upper: Infinity, fixedQuota: 117912.32, rate: 0.35 },
];

/**
 * Calcula ISR mensual retenido sobre un pago bruto asimilado.
 *   - Si bruto ≤ 0 → 0
 *   - Devuelve centavos, redondeado a la unidad
 *
 * Ejemplos (con tarifa 2024):
 *   brutoCents = 752130 ($7,521.30) → bracket 6,332.06-11,128.01:
 *     ISR = 371.83 + (7521.30 - 6332.06) * 0.1088
 *         = 371.83 + 1189.24 * 0.1088
 *         = 371.83 + 129.39 = 501.22 → 50122 cents
 *   brutoCents = 1500000 ($15,000) → bracket 12,935.83-15,487.71:
 *     ISR = 1182.88 + (15000 - 12935.83) * 0.1792 = 1182.88 + 369.90 = 1552.78
 */
export function calcIsrAsimiladoMensual(brutoCents: number): number {
  const bruto = brutoCents / 100;
  if (bruto <= 0) return 0;
  const bracket = ART96_BRACKETS.find((b) => bruto >= b.lower && bruto <= b.upper);
  if (!bracket) return 0;
  const isrMxn = bracket.fixedQuota + (bruto - bracket.lower) * bracket.rate;
  return Math.round(isrMxn * 100);
}

/**
 * Inversa de `calcIsrAsimiladoMensual`: dado el NETO (lo que recibe el
 * trabajador y coincide con el total de la transferencia bancaria),
 * recupera el BRUTO mensual asumiendo la tarifa Art. 96.
 *
 * Por construcción de la tarifa:
 *   neto = bruto - (cuotaFija + (bruto - lower) * tasa)
 *        = bruto·(1 - tasa) - cuotaFija + lower·tasa
 *   ⇒ bruto = (neto + cuotaFija - lower·tasa) / (1 - tasa)
 *
 * Para cada bracket calculamos el NETO máximo (cuando bruto=upper) y
 * elegimos el bracket donde cae el `netoCents`. Luego resolvemos.
 *
 * Retorna 0 si `netoCents` ≤ 0.
 */
export function calcBrutoFromNetoAsimilado(netoCents: number): number {
  const neto = netoCents / 100;
  if (neto <= 0) return 0;
  for (const b of ART96_BRACKETS) {
    // NETO máximo dentro de este bracket = upper - ISR(upper).
    const netoUpper =
      b.upper === Infinity ? Infinity : b.upper - (b.fixedQuota + (b.upper - b.lower) * b.rate);
    if (neto <= netoUpper) {
      const bruto = (neto + b.fixedQuota - b.lower * b.rate) / (1 - b.rate);
      return Math.round(bruto * 100);
    }
  }
  // No debería caer acá (último bracket llega a Infinity), pero por
  // seguridad devolvemos el último cálculo.
  const last = ART96_BRACKETS[ART96_BRACKETS.length - 1]!;
  const bruto = (neto + last.fixedQuota - last.lower * last.rate) / (1 - last.rate);
  return Math.round(bruto * 100);
}
